
import { Vue, Component, Prop, PropSync } from 'vue-property-decorator';
import { Moment } from 'moment';

import Badge from '@/components/common/BadgeNew.vue';
import BadgeColor from '@/entities/BadgeColor';
import EditProjectAction from '@/components/projects/EditProjectActionNew.vue';
import AddTaskAction from '@/components/projects/activities/AddTaskAction.vue';
import MessageAction from '@/components/projects/activities/MessageAction.vue';

import CardContainer from '@/components/card/CardContainer.vue';
import DefaultProjectImage from '@/components/card/DefaultProjectImage.vue';
import CardTitleContainer from '@/components/card/CardTitleContainer.vue';
import RouteParams from '@/entities/RouteParams';
import { Project, User } from '@/generated/graphql';
import RouteNames from '@/entities/RouteNames';
import CardSection from '@/components/card/CardSection.vue';
import CardItemBaseContainer from '@/components/card/CardItemBaseContainer.vue';
import CalendarIcon from '@/components/icons/LocationIconNew.vue';
import ShortenedText from '@/components/common/ShortenedText.vue';
import CreateGroupConversation from '@/components/inbox/CreateGroupConversation.vue';
import ProjectActivityAction from '@/components/projects/activities/ProjectActivityAction.vue';
import CardSingleChip from '@/components/card/CardSingleChip.vue';
import ConversationUserDataForModal from '@/entities/ConversationUserDataForModal';
import Roles from '@/entities/Roles';
import CardItemTitle from '@/components/card/CardItemTitle.vue';
import ProjectStatus from '@/components/projects/ProjectStatus.vue';
import isHoursUnconfirmed from '@/helpers/project/isHoursUnconfirmed';
import ContainerSize from '@/components/uiSize/ContainerSize.vue';
import { hslColorFromName } from "@/utils/hslColorFromName";
import moment from 'moment';

@Component({
	components: {
		ContainerSize,
		ProjectStatus,
		CardItemTitle,
		CardSingleChip,
		ProjectActivityAction,
		CreateGroupConversation,
		ShortenedText,
		CardItemBaseContainer,
		CardSection,
		CardTitleContainer,
		DefaultProjectImage,
		CardContainer,
		BadgeColor,
		CalendarIcon,
		Badge,
		EditProjectAction,
		AddTaskAction,
		MessageAction,
	},
})
export default class ProjectsListCard extends Vue {
	@Prop({ type: Object, required: true }) readonly currentUser!: User;
	@Prop({ type: Object, required: true }) readonly project!: Project;
	@Prop({ type: String, required: true }) readonly companyId!: string;
	@Prop({ type: Boolean, default: false }) readonly hideManager!: boolean;
	@PropSync('descriptionBlockHeight', { type: Number, default: undefined })
	descriptionBlockHeightValue?: number;

	BadgeColor: typeof BadgeColor = BadgeColor;
	RouteNames = RouteNames;

	get hasUnconfirmedHours(): boolean {
		return isHoursUnconfirmed(this.project.project_activities);
	}
	get location(): string {
		const address = this.project.address;
		return address && address.trim();
	}
	get createdAt(): string {
		const date = moment.utc(this.project.created_at).format('DD MMM, YYYY');
		return `${date}`;
	}

	get uniqueSkills() {
  if (!this.project?.project_skills) return [];

  const seen = new Map();
  return this.project.project_skills.filter(item => {
    const skillName = item.skill?.id;
    if (skillName && !seen.has(skillName)) {
      seen.set(skillName, true);
      return true;
    }
    return false;
  });
}


	 get googleMapsLink(): string {
     const baseMapsUrl = "https://www.google.com/maps/search/?api=1";
      const queryParam = `query=${encodeURIComponent(this.location)}`;
      return `${baseMapsUrl}&${queryParam}`;
    }
	get isManagerShown(): boolean {
		if (this.hideManager) {
			return false;
		}

		return this.isAdmin;
	}

	get isAdmin(): boolean {
		return this.currentUser.role === Roles.ADMIN;
	}

	get managerRoute() {
		return {
				name: RouteNames.MANAGER_ABOUT,
				params: {
					id: this.project.manager.id,
				},
		};
	}

	get projectActivitiesPageRoute(): RouteParams<{ id: string }> {
		return {
			name: RouteNames.PROJECT_ACTIVITY,
			params: {
				id: this.project.id,
			},
		};
	}

	get projectDetailsPageRoute(): RouteParams<{ id: string }> {
		return {
			name: RouteNames.PROJECT_DETAILS,
			params: {
				id: this.project.id,
			},
		};
	}

  get initial(): string {
    const firstName = this.project.manager.user?.given_name;
    const lastName = this.project.manager.user?.family_name;

    if (!firstName && !lastName) {
      return ''
    }

    const givenName = firstName ? firstName.trimLeft()[0] : '';
    const familyName = lastName ? lastName.trimLeft()[0] : '';
    return `${givenName}${familyName}`;
  }

  get bgStyle(): object {
      return {
          background: hslColorFromName(this.initial || ""),
      };
  }

	get totalActivities(): string | undefined {
		const getActiveTasks = this.project.project_activities.filter((item) => !item.deleted && item.contractor !== null && !item.drafted);
		const activities = getActiveTasks.length;
		if (activities == 0) {
			return '0';
		}
		if (!activities) {
			return undefined;
		}
		return `${activities}`;
	}

	
	get hasParticipants(): boolean {
		return this.projectUsers.length > 0;
	}

	get projectUsers(): ConversationUserDataForModal[] {
		const manager = this.project.manager?.user;

		if (!manager || this.currentUser.role === Roles.MANAGER) {
			return this.projectContractors;
		}

		return [
			...this.projectContractors,
		];
	}

	get projectContractors(): ConversationUserDataForModal[] {
		const { project_activities: activities } = this.project;

		if (!activities.length) {
			return [];
		}

		const uniqueIds = new Set();

		return activities.reduce(
			(arr: ConversationUserDataForModal[], { contractor }) => {
				const user = contractor?.user;
				if (!user) {
					return arr;
				}
				const currentStepContractors: ConversationUserDataForModal[] =
					[];

				if (!uniqueIds.has(user.id)) {
					currentStepContractors.push({
						userId: user.id,
						avatar: user.avatar,
						name: `${user.given_name} ${user.family_name}`,
					});
					uniqueIds.add(user.id);
				}

				return arr.concat(currentStepContractors);
			},
			[]
		);
	}
	get unassignedVisit(): string {
		const getActiveTasks = this.project.project_activities.filter((item) => !item.deleted && item.contractor === null);
		const activities = getActiveTasks.length;
		if (activities == 0) {
			return '0';
		}
		if (!activities) {
			return undefined;
		}
		return `${activities}`;
	}
}
